import { useCallback, useMemo } from "react";
import { Funnel, FunnelX } from "lucide-react";
import { useAssets, useFilteredAssets } from "@/contexts/AssetContext";
import { calculateFleetStats } from "@/lib/asset-data-utils";
import { getStatusText } from "@/lib/status-utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Chip } from "@/components/ui/chip";
import { Combobox } from "@/components/ui/combobox";
import { Drawer } from "@/components/ui/drawer";
import VendorLocationFilters from "@/components/vendor-location-filters";
import {
  ALL_STATUSES,
  HORSEPOWER_RANGES,
  isAllActive,
} from "@/hooks/useFilters.js";
import { cn } from "@/lib/utils.js";
import { countBubble } from "@/lib/status-styles.js";

const OPTIMIZATION_ENABLED_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const HORSEPOWER_COMBO_OPTIONS = HORSEPOWER_RANGES.map(({ label }) => ({
  value: label,
  label,
}));

/**
 * @typedef {Object} FilterComboboxFieldConfig
 * @property {string} filterKey
 * @property {string} label
 * @property {string} optionsSource
 * @property {boolean} multiple
 * @property {string} placeholder
 * @property {string} searchPlaceholder
 * @property {string} emptyText
 * @property {boolean} [nullableBoolean]
 * @property {boolean} [emptyArrayWhenUndefined]
 */

/** @type {ReadonlyArray<FilterComboboxFieldConfig>} */
const FILTER_COMBOBOX_FIELDS = [
  {
    filterKey: "telemetrySource",
    label: "Telemetry:",
    optionsSource: "telemetrySource",
    multiple: true,
    placeholder: "Select telemetry sources...",
    searchPlaceholder: "Search telemetry sources...",
    emptyText: "No telemetry sources found.",
  },
  {
    filterKey: "optimizationEnabled",
    label: "Optimization Enabled:",
    optionsSource: "optimizationEnabled",
    multiple: false,
    placeholder: "All",
    searchPlaceholder: "Search...",
    emptyText: "No option found.",
    nullableBoolean: true,
  },
  {
    filterKey: "horsepowerRange",
    label: "Horsepower:",
    optionsSource: "horsepowerRange",
    multiple: true,
    placeholder: "Select horsepower ranges...",
    searchPlaceholder: "Search ranges...",
    emptyText: "No ranges found.",
    emptyArrayWhenUndefined: true,
  },
  {
    filterKey: "foreman",
    label: "Foreman:",
    optionsSource: "foreman",
    multiple: true,
    placeholder: "Select assigned foreman...",
    searchPlaceholder: "Search assigned foreman...",
    emptyText: "No foremen found.",
  },
  {
    filterKey: "customer",
    label: "Customer:",
    optionsSource: "customer",
    multiple: true,
    placeholder: "Select assigned customers...",
    searchPlaceholder: "Search assigned customers...",
    emptyText: "No customers found.",
  },
  {
    filterKey: "engineModel",
    label: "Engine Model:",
    optionsSource: "engineModel",
    multiple: true,
    placeholder: "Select engine models...",
    searchPlaceholder: "Search engine models...",
    emptyText: "No engine models found.",
  },
  {
    filterKey: "compressorModel",
    label: "Compressor Model:",
    optionsSource: "compressorModel",
    multiple: true,
    placeholder: "Select compressor models...",
    searchPlaceholder: "Search compressor models...",
    emptyText: "No compressor models found.",
  },
];

/**
 * @param {Array<object>|undefined|null} items
 * @param {(item: object) => string|undefined|null} pick
 * @returns {Array<{ value: string, label: string }>}
 */
function uniqueOptions(items, pick) {
  const values = [...new Set((items || []).map(pick).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b)
  );
  return values.map((v) => ({ value: v, label: v }));
}

/**
 * @typedef {Object} FilterDrawerProps
 * @property {Object} filters - Current filter state
 * @property {Function} onFilterChange - Callback when filters change
 * @property {Function} onClearFilters - Callback to clear all filters
 * @property {Function} onStatusClick - (status: string) => void — toggles status filter (same as status cards)
 * @property {string} [searchTerm] - Search term for status counts (default "")
 * @property {string} [className] - Additional CSS classes for the container
 */

/**
 * Reusable filter drawer component for vendor, location, and telemetry filtering
 * @param {FilterDrawerProps} props - Component props
 * @returns {JSX.Element} Filter drawer component
 */
export default function FilterDrawer({
  filters,
  onFilterChange,
  onClearFilters,
  onStatusClick,
  searchTerm = "",
  className = "",
}) {
  const { assets, isLoading } = useAssets();

  const filtersWithoutStatus = useMemo(
    () => ({ ...filters, status: ALL_STATUSES }),
    [filters]
  );
  const { filteredAssets: assetsForStatusCounts } = useFilteredAssets(
    filtersWithoutStatus,
    searchTerm
  );
  const stats = useMemo(
    () => calculateFleetStats(assetsForStatusCounts),
    [assetsForStatusCounts]
  );

  // Only filter by vendor & status for location dropdown (so you can select multiple locations and telemetry sources)
  const filtersVendorAndStatusOnly = {
    vendor: filters.vendor,
    status: filters.status,
  };
  const { filteredAssets } = useFilteredAssets(filtersVendorAndStatusOnly, "");

  // Filter only by vendor, location & status (not telemetry, foreman, customers, or horsepower) so options reflect current vendor/location selection
  const filtersForOtherFilterOptions = {
    ...filters,
    telemetrySource: [],
    foreman: [],
    customer: [],
    engineModel: [],
    compressorModel: [],
    horsepowerRange: [],
  };
  const { filteredAssets: assetsForOtherFilterOptions } = useFilteredAssets(
    filtersForOtherFilterOptions,
    ""
  );

  // Telemetry options from assets matching current vendor and location (and status)
  const telemetrySourceOptions = useMemo(
    () => uniqueOptions(assetsForOtherFilterOptions, (a) => a.telemetrySource),
    [assetsForOtherFilterOptions]
  );

  const foremanOptions = useMemo(
    () => uniqueOptions(assetsForOtherFilterOptions, (a) => a.foreman),
    [assetsForOtherFilterOptions]
  );

  const customerOptions = useMemo(
    () => uniqueOptions(assetsForOtherFilterOptions, (a) => a.customer),
    [assetsForOtherFilterOptions]
  );

  const engineModelOptions = useMemo(
    () => uniqueOptions(assetsForOtherFilterOptions, (a) => a.engineModel),
    [assetsForOtherFilterOptions]
  );

  const compressorModelOptions = useMemo(
    () => uniqueOptions(assetsForOtherFilterOptions, (a) => a.compressorModel),
    [assetsForOtherFilterOptions]
  );

  const optionsBySource = useMemo(
    () => ({
      telemetrySource: telemetrySourceOptions,
      optimizationEnabled: OPTIMIZATION_ENABLED_OPTIONS,
      horsepowerRange: HORSEPOWER_COMBO_OPTIONS,
      foreman: foremanOptions,
      customer: customerOptions,
      engineModel: engineModelOptions,
      compressorModel: compressorModelOptions,
    }),
    [
      telemetrySourceOptions,
      foremanOptions,
      customerOptions,
      engineModelOptions,
      compressorModelOptions,
    ]
  );

  const activeChips = useMemo(() => {
    const chips = [];

    const arrayKeys = [
      {
        id: "vendor",
        displayName: "Vendor",
      },
      {
        id: "location",
        displayName: "Location",
      },
      {
        id: "telemetrySource",
        displayName: "Telemetry",
      },
      {
        id: "foreman",
        displayName: "Foreman",
      },
      {
        id: "customer",
        displayName: "Customer",
      },
      {
        id: "engineModel",
        displayName: "Engine Model",
      },
      {
        id: "compressorModel",
        displayName: "Compressor Model",
      },
      {
        id: "horsepowerRange",
        displayName: "Horsepower",
      },
    ];

    for (const key of arrayKeys) {
      const arr = filters[key.id] ?? [];
      if (!Array.isArray(arr) || arr.length === 0) continue;
      arr.forEach((value, index) => {
        chips.push({
          id: `${key.id}-${String(value)}-${index}`,
          labelKey: String(key.displayName),
          labelValue: String(value),
          remove: /** @type {const} */ ({
            kind: "arrayField",
            key: String(key.id),
            index,
          }),
        });
      });
    }

    if (!isAllActive(filters.status ?? [])) {
      const selected = filters.status ?? [];
      ALL_STATUSES.forEach((status, index) => {
        if (!selected.includes(status)) return;
        chips.push({
          id: `status-${status}-${index}`,
          labelKey: "Status",
          labelValue: getStatusText(status),
          remove: /** @type {const} */ ({ kind: "status", status }),
        });
      });
    }

    if (filters.optimizationEnabled === true) {
      chips.push({
        id: "optimizationEnabled-true",
        labelKey: "Optimization Enabled",
        labelValue: "Yes",
        remove: /** @type {const} */ ({ kind: "optimization" }),
      });
    } else if (filters.optimizationEnabled === false) {
      chips.push({
        id: "optimizationEnabled-false",
        labelKey: "Optimization Enabled",
        labelValue: "No",
        remove: /** @type {const} */ ({ kind: "optimization" }),
      });
    }

    return chips;
  }, [filters]);

  const handleRemoveChip = useCallback(
    (descriptor) => {
      if (descriptor.kind === "arrayField") {
        const { key, index } = descriptor;
        const arr = filters[key] ?? [];
        if (!Array.isArray(arr) || index < 0 || index >= arr.length) return;
        const next = [...arr];
        next.splice(index, 1);
        onFilterChange(key, next);
        return;
      }
      if (descriptor.kind === "status") {
        const { status } = descriptor;
        const current = filters.status ?? [];
        const newStatuses = current.filter((s) => s !== status);
        if (newStatuses.length === 0) {
          onFilterChange("status", ALL_STATUSES);
        } else {
          onFilterChange("status", newStatuses);
        }
        return;
      }
      if (descriptor.kind === "optimization") {
        onFilterChange("optimizationEnabled", null);
      }
    },
    [filters, onFilterChange]
  );

  const hasActiveChips = activeChips.length > 0;

  return (
    <Drawer side='right'>
      <div
        className={cn(
          hasActiveChips
            ? "grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2"
            : "flex flex-wrap items-center gap-2",
          className
        )}>
        <Drawer.Trigger asChild>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className={cn(
              "border-border bg-background dark:bg-card text-foreground hover:bg-muted dark:hover:bg-muted hover:text-foreground",
              "flex items-center justify-start gap-0 px-3 py-2 rounded-md font-normal h-9 min-h-0 transition-colors shadow-none",
              "[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              hasActiveChips && "shrink-0"
            )}>
            <Funnel className='mr-2 shrink-0' size={16} aria-hidden />
            <span className='font-normal'>Filters</span>
          </Button>
        </Drawer.Trigger>
        {hasActiveChips ? (
          <div className='min-w-0 flex flex-wrap gap-2 items-start'>
            {activeChips.map(({ id, labelKey, labelValue, remove }) => (
              <Chip
                key={id}
                variant='primary'
                size='sm'
                onRemove={() => handleRemoveChip(remove)}>
                <span className='font-bold'>{labelKey}</span>
                <span aria-hidden className='select-none'>
                  :{" "}
                </span>
                <span className='italic font-normal'>{labelValue}</span>
              </Chip>
            ))}
          </div>
        ) : null}
        {hasActiveChips ? (
          <Button
            id='chip-row-clear-filters-button'
            type='button'
            variant='ghost'
            size='sm'
            onClick={onClearFilters}
            className='shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted px-2 py-1 h-8'
            data-testid='button-clear-filters-inline'
            title='Clear all filters'>
            Clear Filters
            <FunnelX className='h-4 w-4' />
          </Button>
        ) : null}
      </div>
      <Drawer.Content>
        <Drawer.Header>Filters</Drawer.Header>

        <Drawer.Body>
          <div className='space-y-4'>
            <div className='flex flex-col space-y-2'>
              <label className='text-sm font-medium text-foreground'>
                Status:
              </label>
              <div className='flex flex-col gap-2 rounded-md border border-border bg-muted/40 dark:bg-muted p-2'>
                {ALL_STATUSES.map((status) => (
                  <div
                    key={status}
                    className='flex items-center gap-2 min-h-[1.25rem]'>
                    <Checkbox
                      id={`filter-drawer-status-${status}`}
                      checked={filters.status.includes(status)}
                      disabled={isLoading}
                      onChange={() => onStatusClick(status)}
                      aria-label={getStatusText(status)}
                    />
                    <label
                      htmlFor={`filter-drawer-status-${status}`}
                      className='text-sm text-muted-foreground cursor-pointer select-none flex-1'>
                      {getStatusText(status)}
                    </label>
                    {isLoading ? (
                      <span
                        className={cn(
                          "ml-auto h-5 min-w-[2rem] animate-pulse rounded-full",
                          countBubble
                        )}
                        aria-hidden
                      />
                    ) : (
                      <Badge
                        variant='secondary'
                        className={cn("ml-auto", countBubble)}>
                        {stats[status] ?? 0}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className='grid grid-cols-1 gap-4 items-end'>
              <VendorLocationFilters
                vendorItems={assets}
                locationItems={filteredAssets}
                getVendor={(asset) => asset?.vendor}
                getLocation={(asset) => asset?.location}
                selectedVendors={filters.vendor}
                selectedLocations={filters.location}
                onVendorsChange={(value) => {
                  onFilterChange("location", []);
                  onFilterChange("vendor", value);
                }}
                onLocationsChange={(value) => onFilterChange("location", value)}
              />

              {FILTER_COMBOBOX_FIELDS.map((field) => (
                <div key={field.filterKey} className='flex flex-col space-y-2'>
                  <label className='text-sm font-medium text-foreground'>
                    {field.label}
                  </label>
                  <Combobox
                    value={
                      field.emptyArrayWhenUndefined
                        ? (filters[field.filterKey] ?? [])
                        : filters[field.filterKey]
                    }
                    onValueChange={(value) =>
                      field.nullableBoolean
                        ? onFilterChange(
                            field.filterKey,
                            value === true || value === false ? value : null
                          )
                        : onFilterChange(field.filterKey, value)
                    }
                    options={optionsBySource[field.optionsSource]}
                    placeholder={field.placeholder}
                    searchPlaceholder={field.searchPlaceholder}
                    emptyText={field.emptyText}
                    multiple={field.multiple}
                  />
                </div>
              ))}
            </div>
          </div>
        </Drawer.Body>

        <Drawer.Footer>
          <Button
            variant='ghost'
            size='sm'
            onClick={onClearFilters}
            className='text-muted-foreground hover:text-foreground hover:bg-muted px-2 py-1 h-8'
            data-testid='button-clear-filters'
            title='Clear all filters'>
            Clear Filters
            <FunnelX className='h-4 w-4' />
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  );
}
